/**
 * @file Socket.tsx
 * @module app/modules/socket/core/Socket
 * @description Implements the Socket integration for real-time socket communication.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.1.1
 * @version 2.1.2
 */

import {
  createContext,
  FC,
  useContext,
  useEffect,
  useState,
} from 'react';
import { io, Socket } from 'socket.io-client';
import { WithChildren } from '../../../../_admin/helpers';
import { API_BASE_URL } from '../../../../utils/constants';
import { getAuth, useAuth } from '../../auth';
type SocketContextType = {
  socket: Socket | undefined;
};
const initialContextValue: SocketContextType = {
  socket: undefined,
};
const SocketContext = createContext<SocketContextType>(initialContextValue);
const useSocket = () => {
  return useContext(SocketContext);
};
const SocketProvider: FC<WithChildren> = ({ children }) => {
  const { auth } = useAuth();
  const [socket, setSocket] = useState<Socket | undefined>();
  useEffect(() => {
    // Skip real-time polling and reconnections during local development.
    if (process.env.REACT_APP_ENV === 'local') {
      return;
    }

    const token = getAuth();
    if (!token) {
      setSocket(undefined);
      return;
    }

    const newSocket = io(API_BASE_URL, {
      query: {
        interface: 'i1',
        token,
      },
    });
    newSocket.on('connect', () => {
      console.log('socket is connected');
    });
    newSocket.on('disconnect', () => {
      console.log('socket is disconnected');
    });
    setSocket(newSocket);

    return () => {
      newSocket.disconnect();
    };
  }, [auth]);
  return (
    <SocketContext.Provider value={{ socket: socket }}>
      {children}
    </SocketContext.Provider>
  );
};
export { SocketProvider, useSocket };
